import { Controller } from "@hotwired/stimulus"
import consumer from "../../channels/consumer"
import { rethrowUnlessAbort } from "../../utils/fetch-error.util"
import { REFRESH_RETRY_MS, refreshWhenIdle, turboRequestInFlight } from "../../utils/turbo-refresh.util"

const RECONCILE_INTERVAL_MS = 15000

// Connects to data-controller="expenses--extraction-status"
// Manages real-time expense extraction status updates via ActionCable
//
// Usage:
//   <div data-controller="expenses--extraction-status"
//        data-expenses--extraction-status-workspace-id-value="123">
//     <!-- Expense rows with extraction status indicators -->
//   </div>
//
// Listens for extraction status updates and updates the UI in real-time
export default class extends Controller {
  static values = {
    workspaceId: Number,
    rowsUrl: String,
    translations: Object,
    badgeClasses: Object
  }

  connect() {
    // Note: Turbo Streams handle table row updates automatically
    // No need for buffering since Turbo handles DOM updates

    if (!this.hasWorkspaceIdValue) {
      console.error("ExpenseExtractionStatus: workspaceId is required")
      return
    }

    // Subscribe to the workspace-specific extraction channel
    this.subscription = consumer.subscriptions.create(
      {
        channel: "ExpenseExtractionChannel",
        workspace_id: this.workspaceIdValue
      },
      {
        connected: this.connected.bind(this),
        disconnected: this.disconnected.bind(this),
        received: this.received.bind(this)
      }
    )

    if (this.hasRowsUrlValue) {
      this.reconcileHandler = () => this.reconcileRows()
      this.beforeStreamHandler = (event) => this.carryRowState(event)
      this.reconcileTimer = setInterval(this.reconcileHandler, RECONCILE_INTERVAL_MS)
      document.addEventListener("visibilitychange", this.reconcileHandler)
      document.addEventListener("turbo:before-stream-render", this.beforeStreamHandler)
    }
  }

  disconnect() {
    clearInterval(this.reconcileTimer)
    this.reconcileAbort?.abort()
    if (this.reconcileHandler) document.removeEventListener("visibilitychange", this.reconcileHandler)
    if (this.beforeStreamHandler) document.removeEventListener("turbo:before-stream-render", this.beforeStreamHandler)

    // Clean up subscription when controller is removed
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
    this.cancelRefresh?.()
    this.cancelAutomaticRender?.()
    clearTimeout(this.panelRefreshTimer)
  }

  // Fires after every (re)subscribe, which is exactly when broadcasts may have
  // been missed: ActionCable does not replay them.
  connected() {
    this.reconcileRows()
  }

  async reconcileRows() {
    if (!this.hasRowsUrlValue || document.hidden || !navigator.onLine) return

    // From the id, not data-record-id: a row prepended by an API or MCP upload is not interactive.
    const ids = Array.from(this.element.querySelectorAll("tr.lc-table__row--extracting"), (row) =>
      row.id.replace("expense_", "")
    )
    if (ids.length === 0) return

    this.reconcileAbort?.abort()
    const abort = new AbortController()
    this.reconcileAbort = abort
    const query = new URLSearchParams(ids.map((id) => ["ids[]", id]))

    try {
      const response = await fetch(`${this.rowsUrlValue}?${query}`, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
        signal: abort.signal
      })
      // A lost session redirects to the sign-in page, which must not land in the table.
      if (response.status !== 200 || !response.headers.get("Content-Type")?.includes("turbo-stream")) return
      const html = await response.text()
      if (abort.signal.aborted || !this.element.isConnected) return
      Turbo.renderStreamMessage(html)
    } catch (error) {
      rethrowUnlessAbort(error)
    }
  }

  // A row rendered outside the list has no page number, and only the row it
  // replaces knows that extraction just finished.
  carryRowState(event) {
    const stream = event.target
    if (stream.getAttribute("action") !== "replace") return

    const current = this.element.querySelector(`:scope > tr[id="${CSS.escape(stream.getAttribute("target") ?? "")}"]`)
    const incoming = stream.querySelector("template")?.content.querySelector("tr")
    if (!current || !incoming) return

    const index = current.querySelector(".lc-table__selection-index")?.textContent
    const incomingIndex = incoming.querySelector(".lc-table__selection-index")
    if (incomingIndex && index) incomingIndex.textContent = index

    const finished =
      current.classList.contains("lc-table__row--extracting") &&
      !incoming.classList.contains("lc-table__row--extracting")
    if (finished) incoming.classList.add("lc-table__row--extracted")
  }

  disconnected() {
    // Disconnected from ExpenseExtractionChannel
  }

  // Handle incoming extraction status updates
  received(data) {
    // Handle Turbo Stream updates - these replace the entire expense row
    if (data.type === "turbo_stream" && data.turbo_stream) {
      Turbo.renderStreamMessage(data.turbo_stream)

      if (this.refreshReviewPanels(data)) return

      // On detail page, refresh to load extracted data into the form
      // Only refresh on terminal states to avoid infinite loops; the refresh
      // waits while the member's own click is in flight
      if (data.expense_id && (data.status === "completed" || data.status === "failed")) {
        const expenseStatusRow = this.statusContainer(data.expense_id)
        if (expenseStatusRow && !this.isRefreshing) {
          this.isRefreshing = true
          this.cancelRefresh = refreshWhenIdle(window.location.href)
        }
      }
      return
    }

    // Handle status updates for detail page (not table row)
    const { expense_id } = data
    const expenseStatusRow = this.statusContainer(expense_id)

    if (this.refreshReviewPanels(data) || data.review_pending) return

    if (expenseStatusRow) {
      // We're on the expense detail page - update status indicator
      this.updateStatusIndicator(expenseStatusRow, data.status, data.extraction_error, data.expense_id)
      const expenseAttemptsRow = document.getElementById(`expense_${expense_id}_attempts`)
      if (expenseAttemptsRow && data.extraction_attempts !== undefined) {
        expenseAttemptsRow.innerText = data.extraction_attempts
      }
      const expenseCompletedRow = document.getElementById(`expense_${expense_id}_completed`)
      if (expenseCompletedRow && data.extraction_completed_at !== undefined) {
        expenseCompletedRow.innerText = data.extraction_completed_at
      }
    }
    // For table rows, Turbo Streams handle the update automatically
  }

  // A pending proposal or unsaved edit keeps the expense form mounted. Only
  // a pristine automatic completion revisits to populate extracted fields.
  refreshReviewPanels(data) {
    if (!data.review_pending && data.status !== "completed" && data.status !== "failed") return false

    const frames = ["proposal", "status_panel"]
      .map((part) => document.getElementById(`expense_${data.expense_id}_extraction_${part}`))
      .filter(Boolean)
    if (frames.length === 0) return false

    clearTimeout(this.panelRefreshTimer)
    const refresh = () => {
      if (turboRequestInFlight()) {
        this.panelRefreshTimer = setTimeout(refresh, REFRESH_RETRY_MS)
        return
      }
      if (!data.review_pending && !this.expenseFormChanged()) {
        if (!this.isRefreshing) {
          this.isRefreshing = true
          this.refreshAutomaticExpense(frames)
        }
        return
      }
      for (const frame of frames) {
        if (frame.hasAttribute("src")) frame.reload()
        else frame.setAttribute("src", frame.dataset.refreshUrl)
      }
      const submit = document.getElementById("expense_submit_button")
      if (submit) {
        submit.disabled = false
        submit.removeAttribute("title")
      }
    }
    refresh()
    return true
  }

  // Limit the final render guard to this automatic visit. A user can start
  // typing after the GET begins, so checking only before Turbo.visit is too early.
  refreshAutomaticExpense(frames) {
    let restoreEditingPosition
    const cleanup = () => {
      document.removeEventListener("turbo:before-render", beforeRender)
      document.removeEventListener("turbo:before-visit", cleanup)
      document.removeEventListener("turbo:submit-start", cleanup)
      document.removeEventListener("turbo:load", loaded)
      this.isRefreshing = false
      this.cancelAutomaticRender = null
    }
    const loaded = () => {
      restoreEditingPosition?.()
      cleanup()
    }
    const beforeRender = (event) => {
      const originalRender = event.detail.render
      event.detail.render = (currentBody, newBody) => {
        if (!this.expenseFormChanged()) return originalRender(currentBody, newBody)

        const focused = document.activeElement
        const selection = [focused?.selectionStart, focused?.selectionEnd]
        const viewport = [window.scrollX, window.scrollY]
        const ancestors = []
        for (let node = document.getElementById("expense_form")?.parentElement; node; node = node.parentElement) {
          ancestors.push([node, node.scrollLeft, node.scrollTop])
        }
        restoreEditingPosition = () => {
          focused?.focus({ preventScroll: true })
          if (typeof selection[0] === "number") focused.setSelectionRange(...selection)
          for (const [node, left, top] of ancestors) {
            node.scrollLeft = left
            node.scrollTop = top
          }
          if (window.scrollX !== viewport[0] || window.scrollY !== viewport[1]) window.scrollTo(...viewport)
        }
        for (const frame of frames) {
          const incoming = newBody.querySelector(`#${frame.id}`)
          if (incoming) frame.replaceChildren(...incoming.childNodes)
        }
        const submit = document.getElementById("expense_submit_button")
        if (submit) {
          submit.disabled = false
          submit.removeAttribute("title")
        }
        restoreEditingPosition()
      }
    }

    this.cancelAutomaticRender = cleanup
    document.addEventListener("turbo:before-render", beforeRender)
    document.addEventListener("turbo:load", loaded)
    this.cancelRefresh = refreshWhenIdle(window.location.href)
    // Register after our visit starts, so later deliberate navigation or saving
    // never inherits the automatic refresh's render guard.
    document.addEventListener("turbo:before-visit", cleanup)
    document.addEventListener("turbo:submit-start", cleanup)
  }

  expenseFormChanged() {
    const form = document.getElementById("expense_form")
    if (!form) return false

    return Array.from(form.elements).some((field) => {
      if (field.tagName === "SELECT") {
        const options = Array.from(field.options)
        let defaults = options.filter((option) => option.defaultSelected).map((option) => option.value)
        if (!field.multiple && options.length > 0) defaults = [defaults.at(-1) ?? options[0].value]
        return JSON.stringify(Array.from(field.selectedOptions, (option) => option.value)) !== JSON.stringify(defaults)
      }
      if (field.type === "checkbox" || field.type === "radio") return field.checked !== field.defaultChecked
      if (field.type === "file") return field.files.length > 0
      // The expense type controller displays an invoice's original datetime as
      // a date. Compare that same date portion without masking real date edits.
      if (field.type === "date") return field.value !== field.defaultValue.split("T")[0]
      return field.defaultValue !== undefined && field.value !== field.defaultValue
    })
  }

  statusContainer(expenseId) {
    return (
      document.getElementById(`expense_${expenseId}_extraction_status`) ||
      document.getElementById(`expense_${expenseId}_status`)
    )
  }

  // Updates the status indicator UI element
  updateStatusIndicator(container, status, error, expenseId) {
    // Clear existing content
    container.innerHTML = ""

    // Create status badge based on status
    const badge = this.createStatusBadge(status, error)
    container.appendChild(badge)

    // Add poll button for processing status
    if (status === "processing") {
      const pollButton = this.createPollButton(expenseId)
      container.appendChild(pollButton)
    }

    // Add retry button for failed status
    if (status === "failed" && error) {
      const retryButton = this.createRetryButton(expenseId)
      container.appendChild(retryButton)
    }

    // A document group (LUC-304) whose merge failed retries the MERGE, not the
    // extraction — there is no merged document to extract from yet.
    if (status === "merge_failed") {
      container.appendChild(this.createRetryMergeButton(expenseId))
    }
  }

  // Creates a status badge element
  createStatusBadge(status, error) {
    const badge = document.createElement("span")
    badge.className = this.getBadgeClass(status)
    badge.setAttribute("data-extraction-status", status)

    // Get status text from translations or use default
    const statusText = this.getStatusText(status)

    const escapedStatusText = this.escapeHtml(statusText)

    if (status === "processing" || status === "merging") {
      // Add spinner for processing status
      badge.innerHTML = `
        <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
        ${escapedStatusText}
      `
    } else if (status === "completed") {
      // Add check icon for completed status
      badge.innerHTML = `
        <span class="material-symbols-outlined" style="font-size: 1rem; vertical-align: text-bottom;">check_circle</span>
        ${escapedStatusText}
      `
    } else if ((status === "failed" || status === "merge_failed") && error) {
      const escapedError = this.escapeHtml(error)
      // Add error icon and tooltip for failed status
      badge.innerHTML = `
        <span class="material-symbols-outlined" style="font-size: 1rem; vertical-align: text-bottom;">error</span>
        ${escapedStatusText}
      `
      badge.setAttribute("title", escapedError)
      badge.setAttribute("data-controller", "tooltip")
      badge.setAttribute("data-tooltip-title-value", escapedError)
    } else {
      badge.textContent = statusText
    }

    return badge
  }

  // Creates a poll button (button_to form) for processing extractions
  createPollButton(expenseId) {
    const form = document.createElement("form")
    form.className = "button_to"
    form.method = "post"
    form.action = `/${this.workspaceIdValue}/expenses/${expenseId}/poll_extraction`

    const button = document.createElement("button")
    button.type = "submit"
    button.className = "btn btn-link btn-sm p-0 ms-1"
    button.title = this.translationsValue.poll_button || "Check extraction status"
    button.innerHTML = '<span class="material-symbols-outlined" style="font-size: 1rem;">refresh</span>'

    const csrfInput = document.createElement("input")
    csrfInput.type = "hidden"
    csrfInput.name = "authenticity_token"
    csrfInput.value = this.getCsrfToken()

    form.appendChild(button)
    form.appendChild(csrfInput)

    return form
  }

  // Creates a retry button for failed extractions
  createRetryButton(expenseId) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "btn btn-sm btn-outline-danger ms-2"
    button.innerHTML = '<span class="material-symbols-outlined" style="font-size: 1rem;">refresh</span>'
    button.setAttribute("data-action", "click->expenses--extraction-status#retry")
    button.setAttribute("data-expense-id", expenseId)
    button.setAttribute("data-workspace-id", this.workspaceIdValue)
    button.setAttribute("title", this.getRetryText())

    return button
  }

  // Creates a "merge again" button for a document group whose merge failed.
  // A plain form post (like the server-rendered button) rather than the fetch the
  // extraction retry uses: the response is a Turbo Stream carrying the flash.
  createRetryMergeButton(expenseId) {
    const form = document.createElement("form")
    form.className = "button_to d-inline-block"
    form.method = "post"
    form.action = `/${this.workspaceIdValue}/expenses/${expenseId}/retry_merge`

    const button = document.createElement("button")
    button.type = "submit"
    button.className = "btn btn-sm btn-outline-danger ms-2"
    button.title = this.translationsValue.retry_merge_button || this.getRetryText()
    button.innerHTML = '<span class="material-symbols-outlined" style="font-size: 1rem;">refresh</span>'

    const csrfInput = document.createElement("input")
    csrfInput.type = "hidden"
    csrfInput.name = "authenticity_token"
    csrfInput.value = this.getCsrfToken()

    form.appendChild(button)
    form.appendChild(csrfInput)

    return form
  }

  // Handles retry button click
  async retry(event) {
    event.preventDefault()
    const button = event.currentTarget
    const expenseId = button.getAttribute("data-expense-id")

    if (!expenseId) return

    // Disable button and show loading state
    button.disabled = true
    button.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span>'

    try {
      const workspaceId = this.workspaceIdValue

      // Send retry request
      const response = await fetch(`/${workspaceId}/expenses/${expenseId}/retry_extraction`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken()
        }
      })

      if (response.ok) {
        // Success - status will be updated via ActionCable
        this.showNotification("success", this.getRetrySuccessText())
      } else {
        throw new Error("Retry failed")
      }
    } catch (error) {
      console.error("Failed to retry extraction:", error)
      this.showNotification("error", this.getRetryErrorText())

      // Re-enable button
      button.disabled = false
      button.innerHTML = '<span class="material-symbols-outlined">refresh</span>'
    }
  }

  // Helper: Get badge CSS class based on status
  // Uses badge classes from Rails helper (passed via data attribute)
  // badgeClassesValue is optional (only used in detail pages, not table view)
  getBadgeClass(status) {
    const baseClass = "badge"
    const variant =
      this.hasBadgeClassesValue && this.badgeClassesValue[status] ? this.badgeClassesValue[status] : "default"
    return `${baseClass} badge--${variant}`
  }

  // Helper: Get status text from translations value
  getStatusText(status) {
    return this.translationsValue[status] || status
  }

  getRetryText() {
    return this.translationsValue.retry_button || "Retry"
  }

  getRetrySuccessText() {
    return this.translationsValue.retry_success || "Extraction restarted"
  }

  getRetryErrorText() {
    return this.translationsValue.retry_error || "Failed to restart extraction"
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // Helper: Get CSRF token
  getCsrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ""
  }

  // Helper: Show notification (uses existing toast system if available)
  showNotification(type, message) {
    // Check if toast controller exists
    const toastEvent = new CustomEvent("toast:show", {
      detail: { type, message }
    })
    document.dispatchEvent(toastEvent)
  }
}
